import { Injectable, inject } from '@angular/core';
import { SpaConfig } from '@tourfold/libraries/spa-config/spa-config';
import {
  SendAiAssistantSessionMessage,
  SendAiAssistantSessionMessageToJSON,
} from '@tourfold/libraries/tf-api-client-frontend-v2';
import {
  REALTIME_AUTH_ADAPTER,
  createAuthenticatedFetch,
} from '@tourfold/shared-kernels/realtime';

@Injectable({
  providedIn: 'root',
})
export class AiAssistantStreamingClient {
  private readonly authAdapter = inject(REALTIME_AUTH_ADAPTER);
  private readonly authenticatedFetch = createAuthenticatedFetch(this.authAdapter);

  streamSessionMessage(
    sessionId: string,
    body: SendAiAssistantSessionMessage,
    signal?: AbortSignal
  ): Promise<Response> {
    return this.authenticatedFetch(this.apiUrl(this.messagePath(sessionId)), {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(SendAiAssistantSessionMessageToJSON(body)),
      signal,
    });
  }

  /**
   * Subscribes to the in-flight turn for [sessionId]. The backend replays already-persisted steps
   * first, then polls for new ones until the turn finalises. Returns immediately (with a normal
   * end-of-stream) when no turn is active.
   *
   * Used to resume after a dropped connection / browser refresh — the backend kept running and
   * persisted every step, so we just need to catch the client UI back up.
   */
  streamActiveTurn(sessionId: string, signal?: AbortSignal): Promise<Response> {
    return this.authenticatedFetch(this.apiUrl(this.activeTurnPath(sessionId)), {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
      },
      signal,
    });
  }

  private messagePath(sessionId: string): string {
    return `/frontend/v2/ai-assistant/sessions/${encodeURIComponent(sessionId)}/messages`;
  }

  private activeTurnPath(sessionId: string): string {
    return `/frontend/v2/ai-assistant/sessions/${encodeURIComponent(sessionId)}/active-turn/stream`;
  }

  private apiUrl(path: string): string {
    const rootUrl = (SpaConfig.apiUrlProvider.rootUrl ?? window.location.origin).replace(
      /\/+$/,
      ''
    );
    return `${rootUrl}${path}`;
  }
}
